import React, { useEffect, useMemo, useState, useRef } from "react";
import { Container, Text, Sprite, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import { TextStyle, TextMetrics } from "pixi.js";
import { useSelector } from "react-redux";
import anime from "animejs";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { ASSET_NAME } from "../../../../assets";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { PixiSettings } from "../../../../pixiSettings";

// Connection popup timeout settings
const CONNECTION_POPUP_TIMEOUTS = {
  NATURAL_SPIN_DELAY: 350, // Buffer delay for regular spins (ms)
  MANUAL_STOP_BUFFER: 400, // Buffer delay for manual stops (ms)
};

// Hardcoded connection message
const CONNECTION_MESSAGE =
  "Spin is taking a bit longer due to a slow connection.\nPlease wait...";

// Overlay settings
const OVERLAY_BACKGROUND_ALPHA = 0.6;
const OVERLAY_BACKGROUND_COLOR = 0x090f41;
const OVERLAY_FADE_DURATION = 200;

// Popup animation settings
const POPUP_ANIM_DURATION = 250;

// Popup configuration
const CONNECTION_POPUP_CONFIG = {
  basePadding: 40,
  baseMessageFontSize: 24,
  baseContentWidth: 480,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.55 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.55 },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: { scaleMultiplier: 0.75 },
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.75 },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      [PixiSettings.ORIENTATION.LANDSCAPE]: { scaleMultiplier: 0.9 },
    },
  },
};

const ConnectionPopup = () => {
  const { dimensions, device, orientation } = useDimensions();
  const { connectionPopupActive } = useSelector((state) => state.userInterface);

  const [overlayAlpha, setOverlayAlpha] = useState(0);
  const [visible, setVisible] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const popupContentRef = useRef(null);
  const openAnimRef = useRef(null);
  const closeAnimRef = useRef(null);

  const deviceConfigMemo = useMemo(() => {
    return CONNECTION_POPUP_CONFIG.DEVICE_CONFIGS[device]?.[orientation] ||
           CONNECTION_POPUP_CONFIG.DEVICE_CONFIGS[PixiSettings.DEVICE.DESKTOP][PixiSettings.ORIENTATION.LANDSCAPE];
  }, [device, orientation]);

  const centerX = useMemo(() => dimensions.width / 2, [dimensions.width]);
  const centerY = useMemo(() => dimensions.height / 2, [dimensions.height]);

  const frameTexture = useMemo(() => PIXI.Assets.get(ASSET_NAME.popup_frame_horizontal), []);

  const popupScale = useMemo(
    () => deviceConfigMemo.scaleMultiplier || 1.0,
    [deviceConfigMemo]
  );

  const padding = useMemo(() => CONNECTION_POPUP_CONFIG.basePadding * popupScale, [popupScale]);
  const messageFontSize = useMemo(() => Math.round(CONNECTION_POPUP_CONFIG.baseMessageFontSize * popupScale), [popupScale]);
  const contentWidth = useMemo(() => CONNECTION_POPUP_CONFIG.baseContentWidth * popupScale, [popupScale]);

  const messageStyle = useMemo(() => new TextStyle({
    fontFamily: FONT_CONFIG.PRIMARY,
    fontSize: messageFontSize,
    fontWeight: "700",
    fill: 0xffffff,
    align: "center",
    wordWrap: true,
    wordWrapWidth: contentWidth,
  }), [messageFontSize, contentWidth]);

  const textMetrics = useMemo(() => TextMetrics.measureText(CONNECTION_MESSAGE, messageStyle), [messageStyle]);
  const textHeight = useMemo(() => textMetrics.height, [textMetrics.height]);

  // Frame scaling — fit content with padding, no buttons needed
  const desiredPanelWidth = useMemo(() => contentWidth + padding * 2, [contentWidth, padding]);
  const desiredPanelHeight = useMemo(
    () => padding + textHeight + padding,
    [textHeight, padding]
  );

  const frameScale = useMemo(() => {
    if (!frameTexture) return 1;
    const scaleX = desiredPanelWidth / frameTexture.width;
    const scaleY = desiredPanelHeight / frameTexture.height;
    return Math.max(scaleX, scaleY);
  }, [frameTexture, desiredPanelWidth, desiredPanelHeight]);

  const frameSpriteWidth = useMemo(() => frameTexture ? frameTexture.width * frameScale : 0, [frameTexture, frameScale]);
  const frameSpriteHeight = useMemo(() => frameTexture ? frameTexture.height * frameScale : 0, [frameTexture, frameScale]);

  const drawOverlay = useMemo(() => (g) => {
    g.clear();
    g.beginFill(OVERLAY_BACKGROUND_COLOR);
    g.drawRect(0, 0, dimensions.width, dimensions.height);
    g.endFill();
  }, [dimensions.width, dimensions.height]);

  // Open animation
  useEffect(() => {
    if (connectionPopupActive) {
      setVisible(true);

      // Clean up any previous close animation
      if (closeAnimRef.current) {
        closeAnimRef.current.pause();
        closeAnimRef.current = null;
      }

      // 1. Overlay fades in
      openAnimRef.current = anime({
        targets: { alpha: 0 },
        alpha: OVERLAY_BACKGROUND_ALPHA,
        duration: OVERLAY_FADE_DURATION,
        easing: "easeOutQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          // 2. Popup fades in after overlay
          if (popupContentRef.current) {
            popupContentRef.current.alpha = 0;
            popupContentRef.current.scale.set(0.85);
            anime({
              targets: popupContentRef.current,
              alpha: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
              complete: () => {
                setHasAnimated(true);
              },
            });
            anime({
              targets: popupContentRef.current.scale,
              x: 1,
              y: 1,
              duration: POPUP_ANIM_DURATION,
              easing: "easeOutQuad",
            });
          }
        },
      });
    } else if (visible && !connectionPopupActive) {
      // Close animation — popup fades out, then overlay
      if (openAnimRef.current) {
        openAnimRef.current.pause();
        openAnimRef.current = null;
      }

      if (popupContentRef.current) {
        anime({
          targets: popupContentRef.current,
          alpha: 0,
          duration: POPUP_ANIM_DURATION,
          easing: "easeInQuad",
        });
        anime({
          targets: popupContentRef.current.scale,
          x: 0.85,
          y: 0.85,
          duration: POPUP_ANIM_DURATION,
          easing: "easeInQuad",
        });
      }

      closeAnimRef.current = anime({
        targets: { alpha: OVERLAY_BACKGROUND_ALPHA },
        alpha: 0,
        duration: OVERLAY_FADE_DURATION,
        delay: POPUP_ANIM_DURATION,
        easing: "easeInQuad",
        update: (anim) => {
          setOverlayAlpha(anim.animations[0].currentValue);
        },
        complete: () => {
          setOverlayAlpha(0);
          setVisible(false);
          setHasAnimated(false);
          closeAnimRef.current = null;
        },
      });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionPopupActive]);

  if (!visible) {
    return null;
  }

  return (
    <Container name="connection_popup" zIndex={9999} sortableChildren={true}>
      <Graphics
        draw={drawOverlay}
        alpha={overlayAlpha}
        eventMode="static"
        pointerdown={(e) => { e.stopPropagation(); }}
        zIndex={9999}
      />

      <Container
        name="popup_content"
        ref={popupContentRef}
        zIndex={10000}
        x={centerX}
        y={centerY}
        alpha={hasAnimated ? 1 : 0}
        scale={hasAnimated ? 1 : 0.85}
      >
        {/* Frame image background */}
        {frameTexture && (
          <Sprite
            texture={frameTexture}
            anchor={0.5}
            width={frameSpriteWidth}
            height={frameSpriteHeight}
          />
        )}

        {/* Connection message — centered in frame */}
        <Text
          text={CONNECTION_MESSAGE}
          style={messageStyle}
          anchor={0.5}
          x={0}
          y={0}
          resolution={2}
        />
      </Container>
    </Container>
  );
};

export { CONNECTION_POPUP_TIMEOUTS };
export default ConnectionPopup;
